import { useLocation, useRouter } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { resolveActiveRailSection, type RailSectionKey } from "../components/NavRail";
import {
  EMPTY_TOOL_BACK_HISTORY,
  isToolBackShortcut,
  recordToolPlace,
  toolBackTarget,
  type ToolBackHistory,
} from "../components/toolBackHistory";
import { isTerminalFocused } from "../lib/terminalFocus";
import { isMacPlatform } from "../lib/utils";

/** Ctrl/⌘+[ returns to the last place in another rail tool (toolBackHistory). */
export function useToolBackShortcut(): void {
  const router = useRouter();
  const pathname = useLocation({ select: (location) => location.pathname });
  const href = useLocation({ select: (location) => location.href });
  const history = useRef<ToolBackHistory<RailSectionKey>>(EMPTY_TOOL_BACK_HISTORY);

  useEffect(() => {
    history.current = recordToolPlace(history.current, {
      tool: resolveActiveRailSection(pathname),
      href,
    });
  }, [href, pathname]);

  useEffect(() => {
    const isMac = isMacPlatform(navigator.platform);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !isToolBackShortcut(event, isMac) || isTerminalFocused()) return;
      const target = toolBackTarget(history.current);
      if (target === null) return;
      event.preventDefault();
      router.history.push(target);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [router]);
}
